import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { 
  Volume2, VolumeX, Headphones, Music, Activity, 
  Cloud, Drum, ChevronDown, ChevronUp, Loader2,
  Lock, Coins, Crown, Layers, Download, RefreshCw,
  AlertCircle, CheckCircle2, Clock, Sparkles, Save
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Track layer icons
const TRACK_ICONS = {
  melody: Music,
  bass: Activity,
  percussion: Drum,
  ambient: Cloud
};

// Separation job statuses
const JOB_STATUS = {
  QUEUED: 'queued',
  PROCESSING: 'processing',
  READY: 'complete',
  FAILED: 'failed'
};

const MixerPanel = ({ 
  musicId,
  onMixComplete,
  onStemsReady,
  disabled = false
}) => {
  const { token, user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [separating, setSeparating] = useState(false);
  const [separationProgress, setSeparationProgress] = useState(0);
  const [separationMessage, setSeparationMessage] = useState('');
  const [separationStatus, setSeparationStatus] = useState(null);
  const [mixerState, setMixerState] = useState(null);
  const [hasStems, setHasStems] = useState(false);
  const [masterVolume, setMasterVolume] = useState(1.0);
  const [mixing, setMixing] = useState(false);
  
  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  const tier = user?.tier || 'starter';
  const isFounder = user?.is_founder;
  const canUseStemSeparation = isFounder || ['pro', 'elite', 'agency', 'studio'].includes(tier);
  const canUsePanControl = isFounder || ['pro', 'elite', 'agency', 'studio'].includes(tier);

  // Fetch mixer state when panel opens
  const fetchMixerState = useCallback(async () => {
    if (!musicId || !token) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/music/${musicId}/mixer`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMixerState(response.data);
      setHasStems(response.data.has_stems || false);
      setMasterVolume(response.data.master_volume || 1.0);
    } catch (error) {
      console.error('Failed to fetch mixer state:', error);
    } finally {
      setLoading(false);
    }
  }, [musicId, token]);

  // Check separation status with improved status tracking
  const checkSeparationStatus = useCallback(async () => {
    if (!musicId || !token) return;
    
    try {
      const response = await axios.get(`${API}/music/${musicId}/separation-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const status = response.data.status;
      setSeparationStatus(status);
      
      if (status === 'complete') {
        setSeparating(false);
        setSeparationProgress(100);
        setSeparationMessage('Complete!');
        setHasStems(true);
        fetchMixerState();
        
        // Show "Saved to Library" confirmation
        toast.custom((t) => (
          <div className="bg-green-900/90 border border-green-700 rounded-xl p-4 shadow-lg flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
              <Save className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="font-medium text-white">Saved to Library</p>
              <p className="text-sm text-green-300">4 tracks ready for mixing</p>
            </div>
          </div>
        ), { duration: 5000 });
        
        onStemsReady?.();
        return true;
      } else if (status === 'failed') {
        setSeparating(false);
        setSeparationStatus('failed');
        if (response.data.fallback) {
          toast.info(response.data.message || 'Multi-track separation is unavailable — using single track.');
        } else {
          toast.error(response.data.message || 'Separation failed');
        }
        return true;
      } else if (status === 'processing') {
        setSeparationProgress(response.data.progress || 0);
        setSeparationMessage(response.data.message || 'Processing...');
        return false;
      } else if (status === 'queued') {
        setSeparationProgress(5);
        setSeparationMessage('Queued...');
        return false;
      }
    } catch (error) {
      console.error('Status check failed:', error);
    }
    return false;
  }, [musicId, token, fetchMixerState, onStemsReady]);

  // Poll separation status while processing
  useEffect(() => {
    if (!separating) return;
    
    const pollInterval = setInterval(async () => {
      const isDone = await checkSeparationStatus();
      if (isDone) {
        clearInterval(pollInterval);
      }
    }, 2000);
    
    return () => clearInterval(pollInterval);
  }, [separating, checkSeparationStatus]);

  // Initial load
  useEffect(() => {
    if (isOpen && musicId) {
      fetchMixerState();
    }
  }, [isOpen, musicId, fetchMixerState]);

  // Show confirmation modal before starting separation
  const handleSeparateClick = () => {
    if (!canUseStemSeparation) {
      toast.error('Track separation requires Pro+ subscription');
      return;
    }
    setShowConfirmModal(true);
  };

  // Start stem separation after confirmation
  const handleConfirmSeparation = async () => {
    setShowConfirmModal(false);
    
    if (!musicId || !token) return;
    
    setSeparating(true);
    setSeparationStatus('queued');
    setSeparationProgress(5);
    setSeparationMessage('Starting separation...');
    
    try {
      const response = await axios.post(
        `${API}/music/${musicId}/separate-stems`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.status === 'ready' || response.data.cached) {
        // Already separated
        setSeparating(false);
        setSeparationStatus('complete');
        setHasStems(true);
        fetchMixerState();
        toast.success('Tracks ready!');
      } else if (response.data.status === 'unavailable') {
        setSeparating(false);
        setSeparationStatus('failed');
        toast.info(response.data.message);
      } else {
        // Processing started
        setSeparationStatus('processing');
        const creditsMsg = response.data.credits_to_deduct 
          ? `${response.data.credits_to_deduct} credits will be used.` 
          : '';
        toast.success(`Separation started! ${creditsMsg}`);
      }
    } catch (error) {
      setSeparating(false);
      setSeparationStatus('failed');
      if (error.response?.status === 403) {
        toast.error('Track separation requires Pro+ subscription');
      } else if (error.response?.status === 402) {
        toast.error(error.response?.data?.detail || 'Insufficient credits');
      } else {
        toast.error('Failed to start separation');
      }
    }
  };

  // Update track settings
  const updateTrack = async (trackId, updates) => {
    if (!mixerState) return;
    
    // Optimistic update
    const updatedLayers = mixerState.track_layers.map(layer => 
      layer.id === trackId ? { ...layer, ...updates } : layer
    );
    setMixerState({ ...mixerState, track_layers: updatedLayers });
    
    // Send to server
    try {
      const tracksUpdate = { [trackId]: updates };
      await axios.post(
        `${API}/music/${musicId}/mixer/update`,
        { music_id: musicId, tracks: tracksUpdate, master_volume: masterVolume },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Failed to update mixer:', error);
      // Revert on error
      fetchMixerState();
    }
  };

  // Toggle mute
  const toggleMute = (trackId) => {
    const track = mixerState?.track_layers?.find(t => t.id === trackId);
    if (track) {
      updateTrack(trackId, { muted: !track.muted });
    }
  };

  // Toggle solo
  const toggleSolo = (trackId) => {
    const track = mixerState?.track_layers?.find(t => t.id === trackId);
    if (track) {
      updateTrack(trackId, { solo: !track.solo });
    }
  };

  // Update volume
  const updateVolume = (trackId, volume) => {
    updateTrack(trackId, { volume });
  };

  // Update pan
  const updatePan = (trackId, pan) => {
    if (!canUsePanControl) {
      toast.error('Pan control requires Pro+ subscription');
      return;
    }
    updateTrack(trackId, { pan });
  };

  // Update master volume
  const handleMasterVolumeChange = async (value) => {
    setMasterVolume(value);
    
    try {
      await axios.post(
        `${API}/music/${musicId}/mixer/update`,
        { music_id: musicId, tracks: {}, master_volume: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Failed to update master volume:', error);
    }
  };

  // Mix and export
  const handleMixStems = async () => {
    if (!musicId || !token) return;
    
    setMixing(true);
    try {
      const response = await axios.post(
        `${API}/music/${musicId}/mixer/mix`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Mix exported successfully!');
        onMixComplete?.(response.data.mix);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Mix failed');
    } finally {
      setMixing(false);
    }
  };

  return (
    <>
    <div className="collapsible-panel">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="collapsible-trigger"
        disabled={disabled}
        data-testid="mixer-panel-toggle"
      >
        <span className="flex items-center gap-2">
          <Layers className="w-4 h-4" />
          Multi-Track Mixer
          {hasStems && (
            <span className="ml-2 text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" />
              4 Tracks
            </span>
          )}
          {!canUseStemSeparation && (
            <span className="ml-2 text-xs px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded-full flex items-center gap-1">
              <Crown className="w-3 h-3" />
              PRO+
            </span>
          )}
        </span>
        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      
      {isOpen && (
        <div className="collapsible-content animate-fade-in-up space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
              <span className="ml-2 text-zinc-500">Loading mixer...</span>
            </div>
          ) : !hasStems ? (
            /* No stems yet - show separation prompt */
            <div className="space-y-4">
              <div className="p-4 bg-zinc-900/60 border border-zinc-800 rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Layers className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium mb-1">Separate into Tracks</h4>
                    <p className="text-sm text-zinc-400 mb-3">
                      Split your audio into 4 separate layers: Melody, Bass, Percussion, and Ambient. 
                      Mix them independently for professional control.
                    </p>
                    
                    {separating ? (
                      <div className="space-y-3">
                        {/* Status badge */}
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${
                            separationStatus === 'queued' ? 'bg-yellow-500/20 text-yellow-400' :
                            separationStatus === 'processing' ? 'bg-blue-500/20 text-blue-400' :
                            separationStatus === 'complete' ? 'bg-green-500/20 text-green-400' :
                            'bg-zinc-500/20 text-zinc-400'
                          }`}>
                            {separationStatus === 'queued' && <Clock className="w-3 h-3" />}
                            {separationStatus === 'processing' && <Loader2 className="w-3 h-3 animate-spin" />}
                            {separationStatus === 'complete' && <CheckCircle2 className="w-3 h-3" />}
                            {separationStatus === 'queued' ? 'Queued' :
                             separationStatus === 'processing' ? 'Processing' :
                             separationStatus === 'complete' ? 'Complete' : 'Starting'}
                          </span>
                          <span className="text-sm text-zinc-400">{separationMessage}</span>
                        </div>
                        
                        {/* Progress bar */}
                        <div className="w-full bg-zinc-800 rounded-full h-2.5 overflow-hidden">
                          <div 
                            className="bg-gradient-to-r from-primary to-orange-400 h-2.5 rounded-full transition-all duration-500 ease-out"
                            style={{ width: `${separationProgress}%` }}
                          />
                        </div>
                        
                        {/* Progress details */}
                        <div className="flex items-center justify-between text-xs text-zinc-500">
                          <span>{separationProgress}% complete</span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            ~{Math.max(0, Math.ceil((100 - separationProgress) * 0.45))}s remaining
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <Button
                          onClick={handleSeparateClick}
                          disabled={!canUseStemSeparation || disabled}
                          className="bg-primary hover:bg-primary/90"
                          data-testid="separate-stems-btn"
                        >
                          <Sparkles className="w-4 h-4 mr-2" />
                          Separate Tracks
                          {canUseStemSeparation && (
                            <span className="ml-2 text-xs opacity-70 flex items-center gap-1">
                              <Coins className="w-3 h-3" />
                              15
                            </span>
                          )}
                        </Button>
                        
                        {!canUseStemSeparation && (
                          <span className="text-xs text-purple-400 flex items-center gap-1">
                            <Lock className="w-3 h-3" />
                            Requires Pro+
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Preview of what tracks look like */}
              <div className="grid grid-cols-4 gap-2 opacity-50">
                {['melody', 'bass', 'percussion', 'ambient'].map((trackId) => {
                  const Icon = TRACK_ICONS[trackId];
                  return (
                    <div key={trackId} className="p-3 bg-zinc-900/40 border border-zinc-800/50 rounded-lg text-center">
                      <Icon className="w-5 h-5 mx-auto mb-2 text-zinc-600" />
                      <p className="text-xs text-zinc-600 capitalize">{trackId}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            /* Has stems - show mixer */
            <div className="space-y-4">
              {/* Master Volume */}
              <div className="flex items-center gap-4 p-3 bg-zinc-900/60 border border-zinc-800 rounded-xl">
                <Volume2 className="w-5 h-5 text-zinc-400" />
                <span className="text-sm font-medium w-20">Master</span>
                <Slider
                  value={[masterVolume]}
                  onValueChange={(v) => handleMasterVolumeChange(v[0])}
                  min={0}
                  max={1}
                  step={0.01}
                  className="flex-1"
                />
                <span className="text-xs text-zinc-500 w-12 text-right">
                  {Math.round(masterVolume * 100)}%
                </span>
              </div>
              
              {/* Track Layers */}
              <div className="space-y-2">
                {mixerState?.track_layers?.map((track) => {
                  const Icon = TRACK_ICONS[track.id] || Music;
                  const isActive = !track.muted && (!mixerState.track_layers.some(t => t.solo) || track.solo);
                  
                  return (
                    <div 
                      key={track.id}
                      className={`p-3 bg-zinc-900/60 border rounded-xl transition-all ${
                        track.muted ? 'border-zinc-800/50 opacity-50' : 
                        track.solo ? 'border-primary/50 ring-1 ring-primary/20' : 'border-zinc-800'
                      }`}
                      data-testid={`track-layer-${track.id}`}
                    >
                      <div className="flex items-center gap-3">
                        {/* Track Icon & Name */}
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${track.color}20` }}
                        >
                          <Icon className="w-5 h-5" style={{ color: track.color }} />
                        </div>
                        
                        <div className="flex-shrink-0 w-24">
                          <p className="font-medium text-sm">{track.name}</p>
                          <p className="text-xs text-zinc-500">{track.id}</p>
                        </div>
                        
                        {/* Mute/Solo */}
                        <div className="flex gap-1">
                          <button
                            onClick={() => toggleMute(track.id)}
                            className={`p-2 rounded-lg transition-all ${
                              track.muted 
                                ? 'bg-red-500/20 text-red-400' 
                                : 'bg-zinc-800 text-zinc-400 hover:text-white'
                            }`}
                            title="Mute"
                            data-testid={`mute-${track.id}`}
                          >
                            <VolumeX className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => toggleSolo(track.id)}
                            className={`p-2 rounded-lg transition-all ${
                              track.solo 
                                ? 'bg-yellow-500/20 text-yellow-400' 
                                : 'bg-zinc-800 text-zinc-400 hover:text-white'
                            }`}
                            title="Solo"
                            data-testid={`solo-${track.id}`}
                          >
                            <Headphones className="w-4 h-4" />
                          </button>
                        </div>
                        
                        {/* Volume Slider */}
                        <div className="flex-1 flex items-center gap-2">
                          <Volume2 className="w-4 h-4 text-zinc-500" />
                          <Slider
                            value={[track.volume || 1]}
                            onValueChange={(v) => updateVolume(track.id, v[0])}
                            min={0}
                            max={1}
                            step={0.01}
                            className="flex-1"
                            disabled={track.muted}
                          />
                          <span className="text-xs text-zinc-500 w-10 text-right">
                            {Math.round((track.volume || 1) * 100)}%
                          </span>
                        </div>
                        
                        {/* Pan Control (Pro+) */}
                        <div className="flex items-center gap-2 w-32 border-l border-zinc-800 pl-3">
                          {canUsePanControl ? (
                            <>
                              <span className="text-xs text-zinc-500">L</span>
                              <Slider
                                value={[track.pan || 0]}
                                onValueChange={(v) => updatePan(track.id, v[0])}
                                min={-1}
                                max={1}
                                step={0.1}
                                className="flex-1"
                                disabled={track.muted}
                              />
                              <span className="text-xs text-zinc-500">R</span>
                            </>
                          ) : (
                            <span className="text-xs text-zinc-500 flex items-center gap-1">
                              <Lock className="w-3 h-3" />
                              Pan (Pro+)
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Mix Actions */}
              <div className="flex gap-3 pt-4 border-t border-zinc-800">
                <Button
                  onClick={handleMixStems}
                  disabled={mixing || disabled}
                  className="flex-1"
                  data-testid="export-mix-btn"
                >
                  {mixing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Mixing...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Export Mix
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={fetchMixerState}
                  disabled={loading}
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Founder badge */}
              {isFounder && (
                <p className="text-xs text-amber-400 flex items-center gap-1">
                  <Crown className="w-3 h-3" />
                  Founder: Full mixer access
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
    
    {/* Confirmation Modal */}
    <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
      <DialogContent className="sm:max-w-md bg-zinc-900 border-zinc-800">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Layers className="w-5 h-5 text-primary" />
            Separate into Tracks
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            This will split your audio into 4 separate layers: Melody, Bass, Percussion, and Ambient.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Processing time warning */}
          <div className="flex items-start gap-3 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
            <Clock className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-400">Processing Time</p>
              <p className="text-xs text-zinc-400">
                Track separation typically takes 30-60 seconds. You can continue using the app while it processes.
              </p>
            </div>
          </div>
          
          {/* Credit cost info */}
          <div className="flex items-start gap-3 p-3 bg-zinc-800/50 border border-zinc-700 rounded-lg">
            <Coins className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-white">Credit Cost</p>
              <p className="text-xs text-zinc-400">
                This operation will use <span className="text-primary font-medium">15 credits</span> from your account.
              </p>
            </div>
          </div>
          
          {/* What you'll get */}
          <div className="grid grid-cols-4 gap-2">
            {['melody', 'bass', 'percussion', 'ambient'].map((trackId) => {
              const Icon = TRACK_ICONS[trackId];
              return (
                <div key={trackId} className="p-2 bg-zinc-800/50 rounded-lg text-center">
                  <Icon className="w-4 h-4 mx-auto mb-1 text-zinc-400" />
                  <p className="text-xs text-zinc-500 capitalize">{trackId}</p>
                </div>
              );
            })}
          </div>
        </div>
        
        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowConfirmModal(false)}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSeparation}
            className="flex-1 bg-primary hover:bg-primary/90"
            data-testid="confirm-separation-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Start Separation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default MixerPanel;
